import type { ApplicationView } from '../app-view.js';

export const SETTINGS_SECTION_IDS = [
  'ninja-backup-inventory',
  'email-delivery',
  'primary-recipients',
  'additional-groups',
  'device-group-assignments',
  'authentication',
  'integrations',
] as const;

export type SettingsSectionId = (typeof SETTINGS_SECTION_IDS)[number];
export const CLIENT_SECTION_IDS = [
  'overview', 'integrations', 'backups', 'm365', '3cx', 'pc-health', 'onboarding', 'projects',
] as const;
export type ClientSectionId = (typeof CLIENT_SECTION_IDS)[number];

export interface FrontendRoute {
  view: ApplicationView;
  settingsSection: SettingsSectionId | null;
  clientId?: string;
  clientSection?: ClientSectionId;
  onboardingRequestNumber?: string;
}

export function parseFrontendRoute(location: string): FrontendRoute {
  const [pathnamePart = '', hashPart = ''] = location.split('#', 2);
  const fragment = hashPart || (pathnamePart.startsWith('#') ? pathnamePart.slice(1) : '');
  if (fragment !== '') return parseLegacyFragment(fragment);

  const pathname = normalizePathname(pathnamePart);
  const clientMatch = pathname.match(/^\/clients\/([0-9a-f-]{36}|ninjaone-bootstrap-[1-9][0-9]*)(?:\/([^/]+))?$/i);
  if (clientMatch !== null) {
    const clientSection = clientMatch[2];
    if (clientSection !== undefined && !isClientSectionId(clientSection)) {
      return { view: 'OVERVIEW', settingsSection: null };
    }
    return {
      view: 'CLIENT_DETAIL', settingsSection: null, clientId: clientMatch[1] ?? '',
      ...(clientSection === undefined ? {} : { clientSection }),
    };
  }
  if (pathname === '/clients') return { view: 'CLIENTS', settingsSection: null };
  if (pathname === '/backups') return { view: 'BACKUPS', settingsSection: null };
  if (pathname === '/servers') return { view: 'SERVERS', settingsSection: null };
  if (pathname === '/m365') return { view: 'MICROSOFT_365', settingsSection: null };
  if (pathname === '/3cx') return { view: 'THREECX', settingsSection: null };
  if (pathname === '/onboarding') return { view: 'ONBOARDING', settingsSection: null };
  const onboardingMatch = pathname.match(/^\/onboarding\/(ONB-[0-9]{6})$/);
  if (onboardingMatch !== null) return {
    view: 'ONBOARDING', settingsSection: null,
    onboardingRequestNumber: onboardingMatch[1]!,
  };
  if (pathname === '/projects') return { view: 'PROJECTS', settingsSection: null };
  if (pathname === '/platform-admin') return { view: 'PLATFORM_ADMIN', settingsSection: null };
  if (pathname === '/settings') return { view: 'SETTINGS', settingsSection: null };
  const settingsMatch = pathname.match(/^\/settings\/([^/]+)$/);
  if (settingsMatch !== null) {
    return {
      view: 'SETTINGS',
      settingsSection: isSettingsSectionId(settingsMatch[1] ?? '') ? settingsMatch[1] as SettingsSectionId : null,
    };
  }
  return { view: 'OVERVIEW', settingsSection: null };
}

function parseLegacyFragment(fragment: string): FrontendRoute {
  if (fragment === 'clients') return { view: 'CLIENTS', settingsSection: null };
  if (fragment === 'backups') return { view: 'BACKUPS', settingsSection: null };
  if (fragment === 'servers') return { view: 'SERVERS', settingsSection: null };
  if (fragment === 'm365') return { view: 'MICROSOFT_365', settingsSection: null };
  if (fragment === '3cx') return { view: 'THREECX', settingsSection: null };
  if (fragment === 'onboarding') return { view: 'ONBOARDING', settingsSection: null };
  if (fragment === 'projects') return { view: 'PROJECTS', settingsSection: null };
  if (fragment === 'platform-admin') return { view: 'PLATFORM_ADMIN', settingsSection: null };
  if (fragment === 'settings') return { view: 'SETTINGS', settingsSection: null };
  const prefix = 'settings/';
  if (fragment.startsWith(prefix)) {
    const section = fragment.slice(prefix.length);
    if (isSettingsSectionId(section)) {
      return { view: 'SETTINGS', settingsSection: section };
    }
    return { view: 'SETTINGS', settingsSection: null };
  }
  return { view: 'OVERVIEW', settingsSection: null };
}

export function defaultRoutePath(route: '/overview' | '/backups'): '/overview' | '/backups' {
  return route;
}

export function settingsSectionHref(section: SettingsSectionId): string {
  return `/settings/${section}`;
}

export function isSettingsSectionTransition(
  previous: FrontendRoute,
  next: FrontendRoute,
): boolean {
  return previous.view === 'SETTINGS' && next.view === 'SETTINGS';
}

function isSettingsSectionId(value: string): value is SettingsSectionId {
  return (SETTINGS_SECTION_IDS as readonly string[]).includes(value);
}

function isClientSectionId(value: string): value is ClientSectionId {
  return (CLIENT_SECTION_IDS as readonly string[]).includes(value);
}

function normalizePathname(value: string): string {
  const raw = value === '' ? '/' : value;
  const withoutQuery = raw.split('?', 1)[0] ?? '/';
  if (withoutQuery === '/') return '/';
  return `/${withoutQuery.replace(/^\/+|\/+$/g, '')}`;
}
